import { Leaf, Sun, Building2, Users } from "lucide-react";
import { useT } from "@/i18n/LanguageProvider";

/**
 * QualityPillars – editorial "four-pillar" section that
 * gives more weight to four themes the developer wants to showcase:
 *   1. Nachhaltige Bauweise
 *   2. Regenerative Energiequellen
 *   3. Hochwertige Architektur
 *   4. Erfahrenes Projektteam
 *
 * Position: between Hero and Highlights.
 * Style:    cream bg, asymmetric layout (5/12 + 7/12), 2x2 pillar grid.
 */
export const QualityPillars = () => {
    const t = useT();

    const PILLARS = [
        {
            icon: Leaf,
            eyebrow: t({ de: "Nachhaltige Bauweise", en: "Sustainable construction" }),
            title: t({ de: "Effizienzhaus 40", en: "Energy class 40" }),
            text: t({
                de: "Hochgedämmte Hülle, dreifach verglaste Fenster, Luft-Luft-Wärmepumpe je Wohnung. KfW-konformer Standard EH 40 mit eigener Verbrauchszuordnung – langfristig niedrige Betriebskosten.",
                en: "Highly insulated envelope, triple-glazed windows, air-to-air heat pump per apartment. KfW-compliant EH 40 standard with individual consumption tracking – sustainably low running costs.",
            }),
            tags: [
                t({ de: "EH 40", en: "EH 40" }),
                t({ de: "Wärmepumpe", en: "Heat pump" }),
                t({ de: "Dreifach-Verglasung", en: "Triple glazing" }),
            ],
        },
        {
            icon: Sun,
            eyebrow: t({ de: "Regenerative Energie", en: "Renewable energy" }),
            title: t({ de: "PV-Anlage inklusive · Wallbox optional", en: "PV included · wallbox optional" }),
            text: t({
                de: "2 kW Photovoltaik-Anlage inkl. passendem Stromspeicher separat für jede Wohnung. Wallbox an jedem Carport-Stellplatz vorbereitet und auf Wunsch installiert. Konsequent ausgelegt für selbst erzeugten Strom und E-Mobilität.",
                en: "2 kW PV system with a dedicated matching battery storage per apartment. Wallbox prepared at every carport and installed on request. Consistently designed for self-generated electricity and e-mobility.",
            }),
            tags: [
                t({ de: "Photovoltaik 2 kW", en: "2 kW PV system" }),
                t({ de: "Speicher inkl.", en: "Battery included" }),
                t({ de: "Wallbox optional", en: "Wallbox optional" }),
            ],
        },
        {
            icon: Building2,
            eyebrow: t({ de: "Hochwertige Architektur", en: "Premium architecture" }),
            title: t({ de: "Klassisch trifft modern", en: "Classic meets modern" }),
            text: t({
                de: "Entworfen von einem renommierten Wiesbadener Architekturbüro. Klassisches Satteldach mit integriertem Flachbereich, präzise Fassadendetails und großzügige Dachterrassen – Architektur mit Charakter.",
                en: "Designed by a renowned Wiesbaden architectural office. Classical gable roof with integrated flat section, precise façade details and generous roof terraces – architecture with character.",
            }),
            tags: [
                t({ de: "Wiesbadener Büro", en: "Wiesbaden office" }),
                t({ de: "Satteldach + Flach", en: "Gable + flat roof" }),
                t({ de: "Dachterrassen", en: "Roof terraces" }),
            ],
        },
        {
            icon: Users,
            eyebrow: t({ de: "Erfahrenes Projektteam", en: "Experienced project team" }),
            title: t({ de: "Eingespielt · regional · persönlich", en: "Rehearsed · regional · personal" }),
            text: t({
                de: "Hinter G21 steht ein eingespieltes Team aus erfahrenem Bauträger und Architekt mit Track-Record im Rhein-Main-Gebiet. Persönliche Ansprechpartner – keine anonyme Großstruktur.",
                en: "Behind G21 stands a well-rehearsed team of experienced developer and architect with a track record across the Rhine-Main region. Personal contacts – no anonymous corporate structure.",
            }),
            tags: [
                t({ de: "Bauträger", en: "Developer" }),
                t({ de: "Architekt", en: "Architect" }),
                t({ de: "Rhein-Main-Region", en: "Rhine-Main region" }),
            ],
        },
    ];

    return (
        <section
            id="qualitaet"
            className="bg-cream text-ink py-24 sm:py-32 border-b border-stone-200"
            data-testid="quality-pillars-section"
        >
            <div className="container-page">
                <div className="grid lg:grid-cols-12 gap-12 mb-14">
                    <div className="lg:col-span-5">
                        <span className="overline" data-testid="quality-pillars-eyebrow">
                            {t({ de: "Vier Qualitäts-Säulen", en: "Four quality pillars" })}
                        </span>
                        <h2
                            className="h-section mt-4 text-balance"
                            data-testid="quality-pillars-title"
                        >
                            {t({ de: "Was Sie bei G21", en: "What you can expect" })}
                            <span className="italic text-brand-500">
                                {t({ de: " erwarten dürfen.", en: " from G21." })}
                            </span>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 self-end">
                        <p className="body-md">
                            {t({
                                de: "G21 ist nicht \u201EBauträger-Standard\u201C. Wir kombinieren konsequent nachhaltige Bauweise, regenerative Energiequellen, eine hochwertige Architektur und ein erfahrenes Projektteam – für Wohnungen, die heute überzeugen und langfristig Bestand haben.",
                                en: "G21 is not \u2018developer-standard\u2019. We combine sustainable construction, renewable energy sources, premium architecture and an experienced project team – for homes that impress today and last decades.",
                            })}
                        </p>
                    </div>
                </div>

                <div
                    className="grid md:grid-cols-2 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden"
                    data-testid="quality-pillars-grid"
                >
                    {PILLARS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <article
                                key={i}
                                className="bg-cream p-8 lg:p-10 group transition-colors hover:bg-white"
                                data-testid={`quality-pillar-${i}`}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div className="w-14 h-14 flex items-center justify-center bg-ink text-clay-300 group-hover:bg-brand-500 group-hover:text-white transition-colors">
                                        <Icon size={24} strokeWidth={1.5} />
                                    </div>
                                    <span
                                        className="font-body text-[10px] uppercase tracking-[0.22em] text-stone-500"
                                    >
                                        0{i + 1} / 04
                                    </span>
                                </div>
                                <span className="overline text-brand-500">{p.eyebrow}</span>
                                <h3 className="font-heading text-2xl lg:text-3xl text-ink mt-2 leading-snug">
                                    {p.title}
                                </h3>
                                <p className="font-body text-sm text-stone-600 leading-relaxed mt-4">
                                    {p.text}
                                </p>
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {p.tags.map((tag, ti) => (
                                        <span
                                            key={ti}
                                            className="px-3 py-1 rounded-full text-[11px] font-body uppercase tracking-[0.16em] text-stone-700 bg-white border border-stone-200"
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
